import { isFulfilled } from "@reduxjs/toolkit";
import { useCallback, useEffect, useState } from "react";
import { useAppDispatch } from "../../../../../app/hooks";
import {
  fetchCustomerCostCenters,
  fetchCustomerCostCentersByWorkLocationId,
} from "../../../../../features/customer/customerCostCentersSlice";
import { CustomerCostCenterOption } from "./types";
import { CustomerCostCentersResult } from "../../../../../features/customer/types/queries";

type Props = {
  customerCompanyId?: string;
  workLocationId?: string;
  includeInactive?: boolean;
};

/**
 * Hook for fetching customer cost centers by company ID or work location ID.
 * Work location ID takes precedence if both are provided.
 *
 * Note: No autocomplete query implemented in backend.
 * Fetches all cost centers for the company and filters/sorts client-side.
 */
const useCustomerCostCenterAutocomplete = ({
  customerCompanyId,
  includeInactive = false,
  workLocationId,
}: Props) => {
  const [customerCostCenterOptions, setCustomerCostCenterOptions] = useState<
    CustomerCostCenterOption[]
  >([]);

  const dispatch = useAppDispatch();

  const handleOptions = useCallback(
    (data: CustomerCostCentersResult[]) => {
      let options: CustomerCostCenterOption[] = data.map((cc) => ({
        id: cc.customerCCId,
        name: cc.name,
        customerOrgId: cc.customerOrgId,
        active: cc.active,
      }));

      // Filter inactive if needed
      if (!includeInactive) {
        options = options.filter((cc) => cc.active);
      }

      // Sort by name
      options.sort((a, b) => a.name.localeCompare(b.name, "fi"));

      setCustomerCostCenterOptions(options);
    },
    [includeInactive],
  );

  useEffect(() => {
    if (workLocationId) {
      dispatch(
        fetchCustomerCostCentersByWorkLocationId({ workLocationId }),
      ).then((action) => isFulfilled(action) && handleOptions(action.payload));
    } else if (customerCompanyId) {
      dispatch(fetchCustomerCostCenters({ customerCompanyId })).then(
        (action) => isFulfilled(action) && handleOptions(action.payload),
      );
    } else {
      setCustomerCostCenterOptions([]);
    }
  }, [
    customerCompanyId,
    includeInactive,
    workLocationId,
    dispatch,
    handleOptions,
  ]);

  // Auto-reset when customerCompanyId changes
  useEffect(() => {
    setCustomerCostCenterOptions([]);
  }, [customerCompanyId]);

  return {
    customerCostCenterOptions,
  };
};

export default useCustomerCostCenterAutocomplete;
